import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from './api';

const sessionKey = (slug) => `beclick_verified_customer:${encodeURIComponent(slug)}`;
export function readCustomerIdentityToken(slug) {
  try { return sessionStorage.getItem(sessionKey(slug)) || ''; } catch { return ''; }
}
export function saveCustomerIdentityToken(slug, token) {
  try {
    if (token) sessionStorage.setItem(sessionKey(slug), token);
    else sessionStorage.removeItem(sessionKey(slug));
  } catch { /* In-memory session remains usable when browser storage is denied. */ }
}

export default function ClickCollectIdentity({ slug, initialPhone = '', onVerified }) {
  const [phone, setPhone] = useState(initialPhone);
  const [firstName, setFirstName] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const scope = useRef(slug);
  scope.current = slug;
  const active = useRef(true);
  const submitting = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    active.current = true;
    generation.current += 1;
    submitting.current = false;
    setBusy(false); setChallenge(null); setCode(''); setError(''); setPhone(initialPhone); setFirstName('');
    return () => { active.current = false; generation.current += 1; };
    // Initial values are captured per restaurant, not on each parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function submit(event) {
    event.preventDefault();
    if (submitting.current) return;
    const requestSlug = slug;
    const requestGeneration = generation.current;
    const isCurrent = () => active.current && scope.current === requestSlug && generation.current === requestGeneration;
    submitting.current = true;
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API_URL}/api/click-collect/${encodeURIComponent(slug)}/identity/${challenge ? 'verify' : 'request'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(challenge ? { challengeId: challenge.challengeId, code } : { phone, firstName }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw Error(payload.error || 'Vérification impossible.');
      if (!isCurrent()) return;
      if (challenge) {
        if (!payload.token || payload.customer?.loyaltyEnabled !== true) throw Error('Vérification incomplète. Réessayez.');
        saveCustomerIdentityToken(slug, payload.token);
        onVerified(payload);
      } else {
        if (!payload.challengeId) throw Error('Code indisponible. Réessayez.');
        setChallenge(payload);
      }
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Vérification impossible.');
    } finally {
      if (isCurrent()) { submitting.current = false; setBusy(false); }
    }
  }

  return <form onSubmit={submit}>
    <p>Vérifiez votre téléphone pour retrouver vos points. Le code est valable dix minutes.</p>
    {challenge ? <>
      <p>Code envoyé au {challenge.maskedPhone}.</p>
      <label>Code reçu par SMS<input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></label>
    </> : <>
      <label>Téléphone<input type="tel" autoComplete="tel" required value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
      <label>Prénom pour un nouveau compte<input autoComplete="given-name" maxLength={100} value={firstName} onChange={(event) => setFirstName(event.target.value)} /></label>
    </>}
    {error ? <p className="clickcollect-error" role="alert">{error}</p> : null}
    <button className="clickcollect-primary" type="submit" disabled={busy}>{busy ? 'Vérification…' : challenge ? 'Vérifier mon code' : 'Recevoir un code SMS'}</button>
    {challenge ? <button type="button" disabled={busy} onClick={() => { setChallenge(null); setCode(''); setError(''); }}>Demander un autre code</button> : null}
  </form>;
}
